import { useMemo, useState } from "react"

import { format } from "date-fns"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { WarningAmberRoundedIcon } from "@l2r-front/l2r-icons"
import { useNetworksStateContext, useRoleIsReadOnly } from "@l2r-front/l2r-networks"
import { Currency, Roadwork, Task, TaskType } from "@l2r-front/l2r-proptypes"
import { Typography, useIsMobileDevice } from "@l2r-front/l2r-ui"
import { formatNumber } from "@l2r-front/l2r-utils"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { MINIMAL_IMPACT } from "../../constants/impacts"
import { STATUS_DONE } from "../../constants/status"
import { RoadworkTagsList } from "../../containers/RoadworkTagsList"
import { RoadworkTask } from "../../containers/RoadworkTask"
import { RoadworkTaskEditModal } from "../../containers/RoadworkTaskEditModal/RoadworkTaskEditModal"
import { RoadworkYearSelector } from "../../containers/RoadworkYearSelector"
import { RoadworkTaskAddFormPost } from "../../forms/RoadworkTaskForm/RoadworkTaskAddForm.post"

import { AddRoadworkTaskButton } from "../AddRoadworkTaskButton"

import { computeTechniqueImpact } from "./RoadworkDetailsInfo.helpers"
import * as Styled from "./RoadworkDetailsInfo.styled"

type RoadworkDetailsInfoProps = {
    className: string,
    currency: Currency,
    roadwork: Roadwork,
    taskTypes: TaskType[],
    onChangeYear: (year: number) => void,
    onChangeTask: (task: Task) => void,
    onDeleteTask: (taskUuid: string) => void,
    onChangeTags: (tags: string[]) => void,
}

export function RoadworkDetailsInfo(props: RoadworkDetailsInfoProps) {

    const {
        className,
        currency,
        roadwork,
        taskTypes,
        onDeleteTask,
    } = props

    const [addTaskModalOpen, setAddTaskModalOpen] = useState(false)
    const isMobile = useIsMobileDevice()
    const { selectedSubNetworkSlugs } = useNetworksStateContext()
    const readOnly = useRoleIsReadOnly(roadwork?.subNetworks)
    const { t } = useTranslation(I18N_NAMESPACE)

    const isRoadworkEditable = useMemo(() => {
        if (!roadwork) {
            return false
        }
        if (selectedSubNetworkSlugs.length && roadwork.subNetworks) {
            const availableSubnetworksAllowsEdition = roadwork.subNetworks.some(subNetwork => selectedSubNetworkSlugs.includes(subNetwork))
            return !readOnly && availableSubnetworksAllowsEdition
        }
        return !readOnly
    }, [readOnly, roadwork, selectedSubNetworkSlugs])

    const isLocalizedTechnique = useMemo(() => {
        if (!roadwork) {
            return false
        }
        const techniqueImpact = computeTechniqueImpact(roadwork, taskTypes)
        return techniqueImpact === MINIMAL_IMPACT
    }, [roadwork, taskTypes])

    const roadworkParams = useMemo(() => {
        const isRoadworkDone = roadwork?.status === STATUS_DONE
        const canChangeYear = isRoadworkEditable && !isRoadworkDone
        const canEditTasks = !isRoadworkDone && isRoadworkEditable
        const selectedTaskTypes = roadwork?.tasks.map(v => v.taskType)
        const taskTypesLeft = taskTypes
            .filter(v => v.impact !== MINIMAL_IMPACT)
            .filter(v => !selectedTaskTypes?.includes(v.code))
        const roadworkCost = roadwork ? formatNumber(roadwork.technique.cost, 0) : "0"

        return {
            isRoadworkDone,
            canChangeYear,
            canEditTasks,
            selectedTaskTypes,
            taskTypesLeft,
            roadworkCost,
        }
    }, [roadwork, isRoadworkEditable, taskTypes])

    return <>
        <Styled.RoadworkDetailWrapper className={className} id="roadwork-detail" isMobile={isMobile}>
            {roadworkParams.canChangeYear && <>
                <Styled.YearWrapper>
                    {roadworkParams.canChangeYear && isRoadworkEditable ? <>
                        <Typography id="task-form-title" variant="Desktop & Mobile/Heading/H2">{t(I18N_NAMESPACE, "forms.roadworkTaskForm.yearTitle")}</Typography>
                        <RoadworkYearSelector name="year" value={roadwork.year} roadwork={roadwork} />
                    </> : <Typography>{roadwork.year}</Typography>}
                </Styled.YearWrapper>
                <Styled.Wrapper>
                    <Styled.TechniqueWrapper>
                        <Typography id="technique-acronym" variant="Desktop & Mobile/Heading/H2">{roadwork.technique.acronym ?? t(I18N_NAMESPACE, "components.roadworkDetailsForm.emptyTechnique")}</Typography>
                    </Styled.TechniqueWrapper>
                    <Styled.TaskImpactBadge id="technique-impact" impact={roadwork.technique.impact} />
                </Styled.Wrapper>
                <Styled.TechniqueWrapper>
                    <div>
                        <Typography variant="Desktop & Mobile/Heading/H3">{`${t(I18N_NAMESPACE, "components.roadworkDetail.cost")}`}&nbsp;</Typography>
                        <Typography id="technique-cost" variant="Desktop & Mobile/Body/Regular">{roadworkParams.roadworkCost || 0} {currency.symbol}</Typography>
                    </div>
                    {(isRoadworkEditable || roadwork?.tags?.length) ? <RoadworkTagsList roadwork={roadwork} isEditable={isRoadworkEditable && roadwork?.tasks?.length} forceDisplayAddButton={true} /> : null}
                </Styled.TechniqueWrapper>
                {
                    roadwork?.tasks?.length === 0 && <Styled.InfoContainer>
                        <WarningAmberRoundedIcon />
                        <Styled.InfoText variant="Desktop & Mobile/Body/Small">
                            {t(I18N_NAMESPACE, "components.roadworkDetails.roadworkWithNoTask")}
                        </Styled.InfoText>
                    </Styled.InfoContainer>
                }
            </>}
            <div>
                {!roadworkParams.canChangeYear &&
                    <Styled.RoadworkDoneWrapper>
                        <Styled.Year variant="Desktop & Mobile/Body/Regular">{roadwork.year}</Styled.Year>
                        <Styled.DoneInfoContainer id="roadwork-done-info">
                            <Styled.Wrapper>
                                <Styled.Container>
                                    <Typography id="technique-acronym" variant="Desktop & Mobile/Heading/H3">{roadwork.technique.acronym ?? t(I18N_NAMESPACE, "components.roadworkDetailsForm.emptyTechnique")}</Typography>
                                    <Styled.DoneTechniqueWrapper>
                                        <Typography variant="Desktop & Mobile/Heading/H3">{`${t(I18N_NAMESPACE, "components.roadworkDetail.cost")}`}&nbsp;</Typography>
                                        <Typography id="technique-cost" variant="Desktop & Mobile/Body/Regular">{roadworkParams.roadworkCost || 0} {currency?.symbol}</Typography>
                                    </Styled.DoneTechniqueWrapper>
                                </Styled.Container>
                                <Styled.TaskImpactBadge id="technique-impact" impact={roadwork.technique.impact} />
                            </Styled.Wrapper>
                        </Styled.DoneInfoContainer>
                        {(isRoadworkEditable || roadwork?.tags?.length) ? <Styled.DoneRoadworkTagsList roadwork={roadwork} isEditable={isRoadworkEditable} /> : null}
                    </Styled.RoadworkDoneWrapper>
                }
                {
                    roadwork.tasks.map((task, index) => {
                        return <RoadworkTask
                            index={index}
                            onDeleteTask={() => {
                                onDeleteTask(task.uuid)
                            }}
                            isDeletable={roadwork.tasks.length > 1}
                            isEditable={roadworkParams.canEditTasks}
                            key={`task-${index}`}
                            task={task}
                            roadwork={roadwork}
                        />
                    })
                }
                <AddRoadworkTaskButton
                    canAdd={roadworkParams.canEditTasks && roadworkParams.taskTypesLeft.length > 0}
                    isLocalizedTechnique={isLocalizedTechnique}
                    onClick={() => {
                        setAddTaskModalOpen(true)
                    }}
                    taskTypes={taskTypes}
                />
                {
                    roadworkParams.isRoadworkDone &&
                    <Typography variant="Desktop & Mobile/Body/Label & Helper text">
                        {t(I18N_NAMESPACE, "containers.roadworkSectionSidebar.doneInfo", {
                            date: format(new Date(roadwork.doneAt), "dd/MM/yyyy"),
                            user: roadwork.doneBy
                        })}
                    </Typography>
                }
            </div>
        </Styled.RoadworkDetailWrapper>
        {addTaskModalOpen &&
            <RoadworkTaskEditModal
                open={addTaskModalOpen}
                mode="add"
                onClose={() => setAddTaskModalOpen(false)}
                roadwork={roadwork}
                FormComponent={RoadworkTaskAddFormPost} />
        }
    </>
}